import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * Reusable confirmation modal for delete operations
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Handler to close modal
 * @param {Function} props.onConfirm - Handler for confirmation
 * @param {string} props.title - Modal title
 * @param {string} props.message - Confirmation message
 * @param {string} props.confirmText - Confirm button text
 * @param {string} props.variant - Alert variant (danger, warning)
 * @param {boolean} props.isLoading - Whether delete operation is in progress
 */
const DeleteConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item?',
  confirmText = 'Delete',
  variant = 'danger',
  isLoading = false,
}) => {
  const { t } = useTranslation();
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className='text-danger'>⚠️ {title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant={variant} className='mb-3'>
          <strong>{t('skinMoles.deleteConfirmation.warning')}</strong>{' '}
          {t('skinMoles.deleteConfirmation.actionCannotBeUndone')}
        </Alert>

        <p>{message}</p>

        {isLoading && (
          <div className='text-center'>
            <div className='spinner-border spinner-border-sm me-2' role='status'>
              <span className='visually-hidden'>{t('skinMoles.deleteConfirmation.deleting')}</span>
            </div>
            {t('skinMoles.deleteConfirmation.deleting')}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={isLoading}>
          {t('skinMoles.deleteConfirmation.cancel')}
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={isLoading}>
          {isLoading ? 'Deleting...' : confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteConfirmationModal;
